import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CollapseHeader from "../../../core/common/collapse-header";
import Table from "../../../core/common/dataTable/index";
import { toast } from "react-toastify";
import { highlightMatch } from "../../../core/common/highlightmatch";
import PageLoadProgressBar from "../../../core/data/json/pageLoadProgressBar";
import { useColumnOrderSave } from "../../../core/common/useColumnOrderSave"; // For column order saving hook
import ScrollToTop from "../../../core/common/ScrollToTop"; // If you want the scroll-to-top feature
import ModalDeleteEffect from "../../../core/common/ModalDeleteEffect"; // If used in modals
import GridView from "../../../core/common/GridView"; // If you want to add grid view toggle (optional, but present in leadsource)
import {
  fetchAllLeadGivenBy,
  updateLeadGivenBy,
  deleteLeadGivenBy,
  LeadGivenBy,
  createLeadGivenBy,
  exportLeadGivenByExcel,
  exportLeadGivenByPDF,
  exportSelectedLeadGivenByExcel,
  exportSelectedLeadGivenByPDF,
  deleteLeadGivenByBulk,
  importLeadGivenByExcel,
  LeadGivenByImportResult,
} from "../../../api/leadgivenby";
import DateRangePicker from "react-bootstrap-daterangepicker";
import { initialSettings } from "../../../core/common/selectoption/selectoption";
import { Dropdown, Menu, Button, Checkbox } from "antd";
import { DownOutlined, FunnelPlotFilled } from "@ant-design/icons";

// Import validation functions
import {
  validateName,
  validateNoDoubleSpacesLead,
  validateNotOnlySymbolsLead,
  validateNoRepeatedCharactersLead,
  validateNoEdgeSymbols,
  validateNotOnlyNumbersLead,
  ValidationResult,
} from "../../../api/leadValidation"; // Make sure path is correct
import Tip from "../../../core/common/tooltip";
import KpiCard from "../../../core/common/KPIcard";
import AnimatedBadge from "../../../core/common/AnimatedBadge";
import ColumnManagement from "../../../core/common/ColumnManagement";
import SubmoduleHoverCard from "../../../core/common/SubCard2Button";

const LeadsGivenBy = () => {
  const [error, setError] = useState<string | null>(null);
  const [dataGivenBy, setDataGivenBy] = useState<LeadGivenBy[]>([]);
  const [orderedData, setOrderedData] = useState<LeadGivenBy[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [editLeadGivenBy, setEditLeadGivenBy] = useState<LeadGivenBy | null>(
    null
  ); // ✅ Selected data for Edit
  const [deleteLeadGivenById, setDeleteLeadGivenById] = useState<string | null>(
    null
  ); // ✅ Selected ID for Delete
  const [newName, setNewName] = useState<string>("");
  const [newStatus, setNewStatus] = useState<string>("Active");
  const [editName, setEditName] = useState<string>("");
  const [editStatus, setEditStatus] = useState<string>("Active");

  // Validation errors
  const [addNameError, setAddNameError] = useState<string | null>(null);
  const [editNameError, setEditNameError] = useState<string | null>(null);

  // Button control
  const [isCreateValid, setIsCreateValid] = useState(false);
  const [saving, setSaving] = useState(false); // For Add & Edit loading
  const [deleting, setDeleting] = useState(false); // For Delete loading
  // Table & filters
  const [searchText, setSearchText] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const [selectedDateRange, setSelectedDateRange] = useState<
    [Date, Date] | null
  >(null);
  const [activeRangeLabel, setActiveRangeLabel] = useState("Last 30 Days");

  // Sorting options
  const [sortOption, setSortOption] = useState<string>("recently_added");
  const [selectedRows, setSelectedRows] = useState<string[]>([]);

  const [importFile, setImportFile] = useState<File | null>(null);
  const [importing, setImporting] = useState(false);
  const [importResult, setImportResult] =
    useState<LeadGivenByImportResult | null>(null);
  const [viewMode, setViewMode] = useState<'list' | 'grid'>('list');
const defaultColumnOrder = [
  "index",
  "LeadGivenBy_name",
  "created_at",
  "Lead_status",
  "action",
];

const [columnKeys, setColumnKeys] = useState<string[]>(() => {
  const saved = localStorage.getItem("leadgivenbyColumnKeys");
  if (saved) return JSON.parse(saved);
  return defaultColumnOrder;
});
const { SaveModal } = useColumnOrderSave({
  columnKeys,
  localStorageKey: "leadgivenbyColumnKeys",
  defaultOrder: defaultColumnOrder,
});
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState<Record<string, boolean>>(
    {
      index: true,
      LeadGivenBy_name: true,
      created_at: true,
      Lead_status: true,
      action: true,
    }
  );

  const [statusFilter, setStatusFilter] = useState("all");
  const [openAccordion, setOpenAccordion] = useState<string | null>(null);
  const [filterNames, setFilterNames] = useState<string[]>([]);
  const [filterStatuses, setFilterStatuses] = useState<string[]>([]);
  const [filterNamesSearch, setFilterNamesSearch] = useState("");
  const [filterStatusesSearch, setFilterStatusesSearch] = useState("");
  const [filterCreatedAts, setFilterCreatedAts] = useState<string[]>([]);
  const [createdAtSearch, setCreatedAtSearch] = useState("");
  const [alphaFilter, setAlphaFilter] = useState<string>("All");

  const handleAccordionToggle = (key: string) => {
    setOpenAccordion((prev) => (prev === key ? null : key));
  };

  // Checkbox selection toggle
  const handleFilterChange = (
    value: string,
    current: string[],
    setter: React.Dispatch<React.SetStateAction<string[]>>
  ) => {
    if (current.includes(value)) {
      setter(current.filter((v) => v !== value));
    } else {
      setter([...current, value]);
    }
  };

  // ✅ Unique lists with useMemo (like your Lead Industry example)
  const uniqueNames = useMemo(
    () =>
      Array.from(
        new Set(dataGivenBy.map((d) => d.LeadGivenBy_name?.trim() || ""))
      ).filter((n) => n),
    [dataGivenBy]
  );

  const uniqueStatuses = useMemo(
    () =>
      Array.from(
        new Set(dataGivenBy.map((d) => d.Lead_status?.trim() || ""))
      ).filter((s) => s),
    [dataGivenBy]
  );

  // ✅ Unique Created At values formatted as dd/mm/yyyy
  const uniqueCreatedAts = useMemo(() => {
    const formattedDates = dataGivenBy
      .map((d) => {
        if (!d.created_at) return "";
        const date = new Date(d.created_at);
        if (isNaN(date.getTime())) return "";
        return date.toLocaleDateString("en-IN", {
          day: "2-digit",
          month: "short",
          year: "numeric",
        });
      })
      .filter((n) => n);

    return Array.from(new Set(formattedDates));
  }, [dataGivenBy]);

  const handleColumnToggle = (columnKey: string) => {
    setVisibleColumns((prev) => ({
      ...prev,
      [columnKey]: !prev[columnKey],
    }));
  };

  // Reusable validation for Lead GivenBy name
  const validateLeadGivenByName = (
    value: string,
    isEdit: boolean = false
  ): ValidationResult => {
    let res = validateName(value, "Lead GivenBy");

    if (res.valid) res = validateNoDoubleSpacesLead(value);
    if (res.valid) res = validateNotOnlySymbolsLead(value);
    if (res.valid) res = validateNoRepeatedCharactersLead(value);
    if (res.valid) res = validateNoEdgeSymbols(value, "Lead GivenBy");
    if (res.valid) res = validateNotOnlyNumbersLead(value);

    // Duplicate check
    if (res.valid && value.trim()) {
      const lowerValue = value.trim().toLowerCase();
      const exists = dataGivenBy.some((item) => {
        const name = item.LeadGivenBy_name?.trim().toLowerCase();
        if (!name) return false;
        if (
          isEdit &&
          editLeadGivenBy &&
          item.leadgivenby_id === editLeadGivenBy.leadgivenby_id
        ) {
          return false;
        }
        return name === lowerValue;
      });

      if (exists) {
        res = { valid: false, message: "Lead GivenBy already exists." };
      }
    }

    return res;
  };

  // Fetch Lead Given By data
  const loadLeadGivenBy = async () => {
    setLoading(true);
    setError(null); // Reset previous error

    const data = await fetchAllLeadGivenBy();
    setDataGivenBy(data);
    setOrderedData(data);

    setLoading(false);
  };

  useEffect(() => {
    loadLeadGivenBy();
  }, []);
  const runValidation = (
    result: ValidationResult,
    setter: React.Dispatch<React.SetStateAction<string | null>>
  ) => {
    if (!result.valid) {
      setter(result.message);
      return false;
    }
    setter(null);
    return true;
  };
  const handleCreateLeadGivenBy = async () => {
    const result = validateLeadGivenByName(newName);
    setAddNameError(result.valid ? "" : result.message);
    setIsCreateValid(result.valid);

    if (!result.valid) {
      const label = document.querySelector(
        '#add_calls label[for="lead-source-input"]'
      ) as HTMLElement;
      if (label) {
        label.classList.remove("pro-shake");
        void label.offsetHeight;
        label.classList.add("pro-shake");
      }
      return;
    }

    setSaving(true); // ← Start loading

    try {
      const payload = {
        LeadGivenBy_name: newName,
        Lead_status: newStatus,
      };

      const created = await createLeadGivenBy(payload);
      if (created) {
        toast.success("Lead GivenBy created successfully!");
        await loadLeadGivenBy();
        setNewName("");
        setNewStatus("Active");
        setAddNameError("");
        setIsCreateValid(false);

        // Close modal
      }
    } catch (err) {
      // Error already handled by API toast
    } finally {
      setSaving(false); // ← Always stop loading
    }
  };

  const handleEditClick = (record: LeadGivenBy) => {
    setEditLeadGivenBy(record);
    setEditName(record.LeadGivenBy_name || "");
    setEditStatus(record.Lead_status || "Active");
    setEditNameError("");

    // 🔥 Run validation immediately when modal opens
    const res = validateLeadGivenByName(record.LeadGivenBy_name || "", true);
    runValidation(res, setEditNameError);
  };

  const handleSaveEdit = async () => {
    const result = validateLeadGivenByName(editName, true);
    setEditNameError(result.valid ? "" : result.message);

    if (!result.valid) {
      const label = document.querySelector(
        '#edit_calls label[for="edit-lead-source-input"]'
      ) as HTMLElement;
      if (label) {
        label.classList.remove("pro-shake");
        void label.offsetHeight;
        label.classList.add("pro-shake");
      }
      return;
    }

    if (!editLeadGivenBy?.leadgivenby_id) return;

    setSaving(true); // ← Start loading

    try {
      const success = await updateLeadGivenBy(editLeadGivenBy.leadgivenby_id, {
        LeadGivenBy_name: editName,
        Lead_status: editStatus,
      });
      if (success) {
        toast.success("Lead GivenBy updated successfully!");
        loadLeadGivenBy();
      }
    } catch (err) {
      // Error handled in API
    } finally {
      setSaving(false); // ← Stop loading
    }
  };

  const handleConfirmDelete = async () => {
    if (!deleteLeadGivenById) return;

    setDeleting(true); // ← Start loading
    try {
      const success = await deleteLeadGivenBy(deleteLeadGivenById);
      if (success) {
        toast.success("Lead GivenBy deleted successfully!");
        await loadLeadGivenBy();
      }
    } catch (err) {
      // Error already handled in API
    } finally {
      setDeleting(false); // ← Always stop loading
    }
  };

  const filteredData = useMemo(() => {
    const q = searchText.toLowerCase();

    const filtered = dataGivenBy.filter((item) => {
      const name = item.LeadGivenBy_name?.toLowerCase() || "";
      const status = item.Lead_status?.toLowerCase() || "";
      const created = item.created_at
        ? new Date(item.created_at).toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "short",
            year: "numeric",
          })
        : "";

      const alphaMatch =
        alphaFilter === "All" ||
        (item.LeadGivenBy_name || "")
          .toLowerCase()
          .startsWith(alphaFilter.toLowerCase());

      const matchName =
        filterNames.length === 0 ||
        filterNames.includes(item.LeadGivenBy_name || "");

      const matchStatus =
        filterStatuses.length === 0 ||
        filterStatuses.includes(item.Lead_status || "");

      const matchCreated =
        filterCreatedAts.length === 0 || filterCreatedAts.includes(created);
      const matchesSearch =
        name.includes(q) ||
        status.includes(q) ||
        created.toLowerCase().includes(q);

      // Filter by selected date range
      let matchesDate = true;
      if (selectedDateRange && created) {
        const createdDate = new Date(created);
        const [start, end] = selectedDateRange;
        matchesDate = createdDate >= start && createdDate <= end;
      }

      if (statusFilter !== "all" && status !== statusFilter) {
        return false;
      }

      return (
        matchesSearch &&
        matchesDate &&
        matchName &&
        matchStatus &&
        matchCreated &&
        alphaMatch
      );
    });

    // Sort
    const sorted = [...filtered];
    if (sortOption === "ascending") {
      sorted.sort((a, b) =>
        (a.LeadGivenBy_name || "").localeCompare(b.LeadGivenBy_name || "")
      );
    } else if (sortOption === "descending") {
      sorted.sort((a, b) =>
        (b.LeadGivenBy_name || "").localeCompare(a.LeadGivenBy_name || "")
      );
    } else if (sortOption === "recently_added") {
      sorted.sort(
        (a, b) =>
          new Date(b.created_at || "").getTime() -
          new Date(a.created_at || "").getTime()
      );
    }

    return sorted;
  }, [
    searchText,
    dataGivenBy,
    sortOption,
    selectedDateRange,
    statusFilter,
    filterNames,
    filterStatuses,
    filterCreatedAts,
    alphaFilter,
  ]);

  const columns = useMemo(
    () => [
      {
        title: "S.No",
        key: "index",
        width: 80,
        align: "center" as const,
        render: (_: any, __: any, index: number) => (
          <span className="text-muted fw-medium">
            {(currentPage - 1) * pageSize + index + 1}
          </span>
        ),
      },

      {
        title: (
          <div className="d-flex align-items-center justify-content-between">
            <span className="fw-semibold text-dark">Lead GivenBy Name</span>

            {/* Alphabetical Filter */}
            <Dropdown
              trigger={["click"]}
              onOpenChange={(open) =>
                open && setTimeout(() => document.body.click(), 0)
              }
              overlay={
                <Menu
                  selectedKeys={alphaFilter ? [alphaFilter] : ["All"]}
                  onClick={({ key }) =>
                    setAlphaFilter(key === "All" ? "" : key)
                  }
                  style={{ width: 50, maxHeight: 250, overflow: "auto" }}
                >
                  <Menu.Item key="All" className="text-center">
                    All
                  </Menu.Item>
                  {Array.from("ABCDEFGHIJKLMNOPQRSTUVWXYZ").map((letter) => (
                    <Menu.Item key={letter} className="text-center">
                      {letter}
                    </Menu.Item>
                  ))}
                </Menu>
              }
            >
              <Button
                size="small"
                type="text"
                className="border rounded ms-2 px-2 py-1 bg-light hover-bg-white"
                onClick={(e) => e.stopPropagation()}
                title="Filter by first letter"
              >
                <span className="fw-medium text-primary">
                  {alphaFilter && alphaFilter !== "All" ? alphaFilter : "ALL"}
                </span>
                <DownOutlined
                  className="ms-1 text-muted"
                  style={{ fontSize: 10 }}
                />
              </Button>
            </Dropdown>
          </div>
        ),
        dataIndex: "LeadGivenBy_name",
        key: "LeadGivenBy_name",
        sorter: true,
       render: (_: any, record: LeadGivenBy) => (
  <SubmoduleHoverCard
    name={highlightMatch(record.LeadGivenBy_name || "", searchText)}
    role="Lead Given By"
    createdAt={record.created_at}
    variant2={["contactleadGivenBy", "leadGivenBy"]}   // adjust variants as appropriate
  />
),
      },

      {
        title: "Created At",
        dataIndex: "created_at",
        key: "created_at",
        width: 130,
        align: "center" as const,
        sorter: true,
        render: (value: string) => {
          if (!value) return <span className="text-muted">—</span>;

          const date = new Date(value);
          if (isNaN(date.getTime()))
            return <span className="text-muted">Invalid</span>;

          const formattedDate = date.toLocaleDateString("en-IN", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
          });

          return <span className="text-muted">{formattedDate}</span>;
        },
      },

      {
        title: (
          <div className="d-flex align-items-center justify-content-between">
            <span className="fw-semibold text-dark">Status</span>

            {/* Status Filter with Counts */}
            <Dropdown
              trigger={["click"]}
              overlay={
                <Menu
                  className="p-2 shadow-sm border-0"
                  style={{ minWidth: 220 }}
                >
                  <Menu.Item
                    key="all"
                    className="d-flex justify-content-between align-items-center py-2 rounded hover-bg-light"
                    onClick={() => setFilterStatuses([])}
                  >
                    <Checkbox checked={filterStatuses.length === 0}>
                      <div className="d-flex justify-content-between align-items-center">
                        <span className="d-flex align-items-center">
                          All Status
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 48 }}
                        >
                          <span className="badge bg-light text-dark">
                            {dataGivenBy.length}
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>

                  <Menu.Divider className="my-2" />

                  <Menu.Item
                    key="Active"
                    onClick={() => {
                      setFilterStatuses((prev) =>
                        prev.includes("Active")
                          ? prev.filter((v) => v !== "Active")
                          : [...prev, "Active"]
                      );
                    }}
                  >
                    <Checkbox checked={filterStatuses.includes("Active")}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="d-flex align-items-center">
                          Active
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 68 }}
                        >
                          <span className="badge bg-light text-dark">
                            {
                              dataGivenBy.filter(
                                (d) => d.Lead_status === "Active"
                              ).length
                            }
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>

                  <Menu.Item
                    key="Inactive"
                    onClick={() => {
                      setFilterStatuses((prev) =>
                        prev.includes("Inactive")
                          ? prev.filter((v) => v !== "Inactive")
                          : [...prev, "Inactive"]
                      );
                    }}
                  >
                    <Checkbox checked={filterStatuses.includes("Inactive")}>
                      <div className="d-flex justify-content-between align-items-center w-100">
                        <span className="d-flex align-items-center">
                          Inactive
                        </span>
                        <span
                          className="d-flex align-items-center justify-content-end gap-2"
                          style={{ minWidth: 58 }}
                        >
                          <span className="badge bg-light text-dark">
                            {
                              dataGivenBy.filter(
                                (d) => d.Lead_status === "Inactive"
                              ).length
                            }
                          </span>
                        </span>
                      </div>
                    </Checkbox>
                  </Menu.Item>
                </Menu>
              }
            >
              <Button
                size="small"
                type="text"
                className="ms-2"
                style={{ padding: "0 6px" }}
                onClick={(e) => e.stopPropagation()}
                title="Filter by status"
              >
                <FunnelPlotFilled
                  className={
                    filterStatuses.length > 0 ? "text-primary" : "text-muted"
                  }
                  style={{ fontSize: 16 }}
                />
                {filterStatuses.length > 0 && (
                  <span className="badge bg-primary rounded-pill ms-1 fw-bold">
                    {filterStatuses.length}
                  </span>
                )}
              </Button>
            </Dropdown>
          </div>
        ),
        dataIndex: "Lead_status",
        key: "Lead_status",
        width: 130,
        align: "center" as const,
        sorter: true,
        render: (status: string) => {
          const value = status?.toLowerCase();
          const isActive = value === "active";

          const badgeClass = isActive
            ? "bg-success"
            : value === "inactive"
            ? "bg-danger"
            : "bg-secondary";

          const iconClass = isActive
            ? "ti ti-check"
            : value === "inactive"
            ? "ti ti-x"
            : undefined;

          return (
            <AnimatedBadge
              status={status || "N/A"}
              className={`badge-status ${badgeClass}`}
              icon={iconClass}
            />
          );
        },
      },

      {
        title: <span className="fw-semibold text-dark">Actions</span>,
        key: "action",
        fixed: "right" as const,
        width: 90,
        align: "center" as const,
        render: (_: any, record: LeadGivenBy) => {
          const menu = (
            <Menu className="shadow-lg border-0 p-1">
              <Menu.Item
                key="edit"
                className="d-flex align-items-center gap-2 py-2 px-3 rounded hover-bg-light"
                data-bs-toggle="modal"
                data-bs-target="#edit_calls"
               onClick={() => handleEditClick(record)}
              >
                <i className="ti ti-edit text-primary fs-15 me-2" />
                <span className="fw-medium">Edit</span>
              </Menu.Item>

              <Menu.Item
                key="delete"
                className="d-flex align-items-center gap-2 py-2 px-3 rounded text-danger hover-bg-danger-light"
                data-bs-toggle="modal"
                data-bs-target="#delete_calls"
                onClick={() => setDeleteLeadGivenById(record.leadgivenby_id!)}
              >
                <i className="ti ti-trash fs-15 me-2" />
                <span className="fw-medium">Delete</span>
              </Menu.Item>
            </Menu>
          );

          return (
            <Dropdown
              trigger={["click"]}
              overlay={menu}
              placement="bottomRight"
              onOpenChange={(open) =>
                open && setTimeout(() => document.body.click(), 0)
              }
            >
              <Button
                size="small"
                type="text"
                className="table-action-btn"
                onClick={(e) => e.stopPropagation()}
                title="More actions"
              >
                <i className="fa fa-ellipsis-v" />
              </Button>
            </Dropdown>
          );
        },
      },
    ],
    [
      currentPage,
      pageSize,
      searchText,
      alphaFilter,
      filterStatuses,
      dataGivenBy,
    ]
  );

  const handleRowOrderChange = (newOrdered: LeadGivenBy[]) => {
    setOrderedData(newOrdered);
    // OPTIONAL: persist to backend
    // await reorderLeadSources(newOrdered.map(r => r.LeadSource_id));
  };

  // Add these calculations after uniqueCreatedAts useMemo
  const totalCount = useMemo(() => dataGivenBy.length, [dataGivenBy]);

  const activeCount = useMemo(
    () => dataGivenBy.filter((d) => d.Lead_status === "Active").length,
    [dataGivenBy]
  );

  const inactiveCount = useMemo(
    () => dataGivenBy.filter((d) => d.Lead_status === "Inactive").length,
    [dataGivenBy]
  );

  const thisMonthCount = useMemo(() => {
    const now = new Date();
    const thisMonthStart = new Date(now.getFullYear(), now.getMonth(), 1);

    return dataGivenBy.filter((item) => {
      if (!item.created_at) return false;
      const date = new Date(item.created_at);
      return date >= thisMonthStart;
    }).length;
  }, [dataGivenBy]);

  const prevMonthCount = useMemo(() => {
    const now = new Date();
    const prevMonth = new Date(now.getFullYear(), now.getMonth() - 1, 1);
    const prevMonthEnd = new Date(now.getFullYear(), now.getMonth(), 0);

    return dataGivenBy.filter((item) => {
      if (!item.created_at) return false;
      const date = new Date(item.created_at);
      return date >= prevMonth && date <= prevMonthEnd;
    }).length;
  }, [dataGivenBy]);

  const thisMonthPercentage = useMemo(() => {
    if (prevMonthCount === 0) return thisMonthCount > 0 ? "100" : "0";
    return (((thisMonthCount - prevMonthCount) / prevMonthCount) * 100).toFixed(
      1
    );
  }, [thisMonthCount, prevMonthCount]);

  const activePercentage = useMemo(() => {
    if (totalCount === 0) return "0";
    return ((activeCount / totalCount) * 100).toFixed(1);
  }, [activeCount, totalCount]);

  const inactivePercentage = useMemo(() => {
    if (totalCount === 0) return "0";
    return ((inactiveCount / totalCount) * 100).toFixed(1);
  }, [inactiveCount, totalCount]);

  const handleExportAllPDF = async () => {
    try {
      const blob = await exportLeadGivenByPDF();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_given_by_all_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // toast handled in API
    }
  };

  const handleExportAllExcel = async () => {
    try {
      const blob = await exportLeadGivenByExcel();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_given_by_all_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // toast handled
    }
  };

  const handleExportSelectedPDF = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one lead given by to export.");
      return;
    }
    try {
      const blob = await exportSelectedLeadGivenByPDF(selectedRows);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_given_by_selected_${selectedRows.length}_${
        new Date().toISOString().split("T")[0]
      }.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // handled in API
    }
  };

  const handleExportSelectedExcel = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one lead given by to export.");
      return;
    }
    try {
      const blob = await exportSelectedLeadGivenByExcel(selectedRows);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `lead_given_by_selected_${selectedRows.length}_${
        new Date().toISOString().split("T")[0]
      }.xlsx`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      // handled in API
    }
  };

  const handleBulkDelete = async () => {
    if (selectedRows.length === 0) {
      toast.warning("Please select at least one lead given by to delete.");
      return;
    }
    setDeleting(true);
    try {
      await deleteLeadGivenByBulk(selectedRows);
      toast.success(
        `${selectedRows.length} Lead Given By${
          selectedRows.length > 1 ? " entries" : ""
        } deleted successfully`
      );
      await loadLeadGivenBy(); // Adjust to your refresh function name
      setSelectedRows([]);
      // const modalEl = document.getElementById("bulk_delete_modal");
      // if (modalEl) {
      //   const modal = Modal.getInstance(modalEl);
      //   modal?.hide();
      // }
    } catch (err) {
      // error toast already in API
    } finally {
      setDeleting(false);
    }
  };
  return (
    <>
      <PageLoadProgressBar />
      {/* Page Wrapper */}
      <div className="page-wrapper">
        <div className="content">
          <div className="row">
            <div className="col-md-12">
              {/* Page Header */}
              <div className="page-header">
                <div className="row align-items-center">
                  <div className="col-sm-8">
                    <h4 className="page-title">
                      <i className="bi bi-person-lines-fill text-primary me-2" />
                      Lead GivenBy{" "}
                      <span className="count-title">{dataGivenBy.length}</span>
                    </h4>
                  </div>
                  <div className="col-sm-4 text-sm-end">
                    <div className="head-icons">
                      <CollapseHeader />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row g-3 mb-4">
                {(() => {
                  const total = totalCount;
                  const activePercent =
                    total > 0 ? ((activeCount / total) * 100).toFixed(1) : 0;
                  const inactivePercent =
                    total > 0 ? ((inactiveCount / total) * 100).toFixed(1) : 0;
                  const thisMonthPercent =
                    total > 0 ? ((thisMonthCount / total) * 100).toFixed(1) : 0;

                  return (
                    <>
                      {/* Total */}
                      <div className="col-lg-3 col-md-6">
                        <Tip
                          text={`Total Lead GivenBy: ${totalCount} (100%)`}
                          placement="top"
                        >
                          <div className="kpi-card-wrapper">
                            <KpiCard
                              title="Lead GivenBy"
                              subtitle="Total"
                              value={totalCount}
                              icon="ti ti-users"
                              color="primary"
                              tooltip="Total number of lead given by in the system"
                            />
                          </div>
                        </Tip>
                      </div>

                      {/* This Month */}
                      <div className="col-lg-3 col-md-6">
                        <Tip
                          text={`Added This Month: ${thisMonthCount} (${thisMonthPercent}%)`}
                          placement="top"
                        >
                          <div className="kpi-card-wrapper">
                            <KpiCard
                              title="Lead GivenBy"
                              subtitle="This Month"
                              value={thisMonthCount}
                              icon="ti ti-calendar"
                              color="warning"
                              tooltip="Lead given by created in the current month"
                              percentage={thisMonthPercentage}
                              percentageColor={
                                parseFloat(thisMonthPercentage) > 0
                                  ? "success"
                                  : "danger"
                              }
                              percentageIcon={
                                parseFloat(thisMonthPercentage) > 0
                                  ? "ti ti-arrow-up"
                                  : "ti ti-arrow-down"
                              }
                            />
                          </div>
                        </Tip>
                      </div>

                      {/* Active */}
                      <div className="col-lg-3 col-md-6">
                        <Tip
                          text={`Active: ${activeCount} (${activePercent}%)`}
                          placement="top"
                        >
                          <div className="kpi-card-wrapper">
                            <KpiCard
                              title="Lead GivenBy"
                              subtitle="Active"
                              value={activeCount}
                              icon="ti-pulse"
                              color="success"
                              tooltip="Currently active and usable lead given by"
                              percentage={activePercentage}
                              percentageColor="success"
                              percentageIcon="ti ti-arrow-up"
                            />
                          </div>
                        </Tip>
                      </div>

                      {/* Inactive */}
                      <div className="col-lg-3 col-md-6">
                        <Tip
                          text={`Inactive: ${inactiveCount} (${inactivePercent}%)`}
                          placement="top"
                        >
                          <div className="kpi-card-wrapper">
                            <KpiCard
                              title="Lead GivenBy"
                              subtitle="Inactive"
                              value={inactiveCount}
                              icon="ti-power-off"
                              color="danger"
                              tooltip="Inactive or archived lead given by"
                              percentage={inactivePercentage}
                              percentageColor="danger"
                              percentageIcon="ti ti-arrow-down"
                            />
                          </div>
                        </Tip>
                      </div>
                    </>
                  );
                })()}
              </div>
              {/* /Page Header */}
              <div className="card">
                <div className="card-header">
                  {/* Search */}
                  <div className="row align-items-center">
                    <div className="col-sm-4">
                      <div className="icon-form mb-3 mb-sm-0 position-relative">
                        <span className="form-icon">
                          <i className="ti ti-search text-muted fs-16" />
                        </span>
                        <input
                          type="text"
                          className="form-control form-control-lg ps-5 rounded-3 border shadow-sm"
                          placeholder="Search Lead Given By..."
                          value={searchText}
                          onChange={(e) => setSearchText(e.target.value)}
                          style={{ height: "42px", fontSize: "14px" }}
                        />

                        {/* Clear button appears when there's text */}
                        {searchText && (
                          <button
                            type="button"
                            className="btn-close position-absolute end-0 top-50 translate-middle-y me-3"
                            style={{ zIndex: 10, fontSize: "12px" }}
                            onClick={() => setSearchText("")}
                            aria-label="Clear search"
                          />
                        )}
                      </div>
                    </div>
                    <div className="col-sm-8">
                      <div className="d-flex align-items-center flex-wrap row-gap-2 justify-content-sm-end">
                        <button
                          type="button"
                          className="btn btn-outline-success d-flex align-items-center gap-2 me-2"
                          data-bs-toggle="modal"
                          aria-label="Import lead given by from Excel"
                          data-bs-target="#import_excel_modal"
                        >
                          <i className="ti ti-file-import fs-16" />
                          <span className="d-none d-sm-inline">
                            Import Excel
                          </span>
                        </button>

                        <div className="dropdown me-3">
                          <button
                            type="button"
                            className={`btn btn-outline-secondary d-flex align-items-center gap-2 dropdown-toggle ${
                              selectedRows.length > 0
                                ? "border-primary text-primary"
                                : ""
                            }`}
                            data-bs-toggle="dropdown"
                            aria-expanded="false"
                            disabled={loading}
                          >
                            <i className="ti ti-package-export fs-16" />
                            <span className="d-none d-sm-inline">Export</span>
                            {selectedRows.length > 0 && (
                              <span className="badge bg-primary rounded-pill ms-2">
                                {selectedRows.length}
                              </span>
                            )}
                          </button>

                          <ul
                            className="dropdown-menu dropdown-menu-end shadow-lg border-0 py-3 px-2"
                            style={{ minWidth: "260px" }}
                          >
                            <li>
                              <div className="px-3 mb-2">
                                <small className="text-uppercase text-muted fw-bold">
                                  Export Options
                                </small>
                              </div>
                            </li>
                            <li>
                              <hr className="dropdown-divider my-1" />
                            </li>
                            <li>
                              <div className="px-3 py-1">
                                <small className="text-muted">
                                  All Records ({filteredData.length})
                                </small>
                              </div>
                            </li>
                            <li>
                              <button
                                className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                onClick={handleExportAllPDF}
                              >
                                <div className="avatar avatar-sm bg-danger-light rounded">
                                  <i className="ti ti-file-type-pdf text-danger fs-14" />
                                </div>
                                <div className="text-start">
                                  <div className="fw-medium">Export as PDF</div>
                                  <small className="text-muted">
                                    Portable Document Format
                                  </small>
                                </div>
                              </button>
                            </li>
                            <li>
                              <button
                                className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                onClick={handleExportAllExcel}
                              >
                                <div className="avatar avatar-sm bg-success-light rounded">
                                  <i className="ti ti-file-spreadsheet text-success fs-14" />
                                </div>
                                <div className="text-start">
                                  <div className="fw-medium">
                                    Export as Excel
                                  </div>
                                  <small className="text-muted">
                                    Spreadsheet (.xlsx)
                                  </small>
                                </div>
                              </button>
                            </li>

                            {selectedRows.length > 0 && (
                              <>
                                <li>
                                  <hr className="dropdown-divider my-2" />
                                </li>
                                <li>
                                  <div className="px-3 py-1">
                                    <small className="text-muted">
                                      Selected Only ({selectedRows.length})
                                    </small>
                                  </div>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                    onClick={handleExportSelectedPDF}
                                  >
                                    <div className="avatar avatar-sm bg-danger-light rounded">
                                      <i className="ti ti-file-type-pdf text-danger fs-14" />
                                    </div>
                                    <div className="text-start">
                                      <div className="fw-medium">
                                        Export Selected as PDF
                                      </div>
                                    </div>
                                  </button>
                                </li>
                                <li>
                                  <button
                                    className="dropdown-item d-flex align-items-center gap-3 py-2 rounded-3"
                                    onClick={handleExportSelectedExcel}
                                  >
                                    <div className="avatar avatar-sm bg-success-light rounded">
                                      <i className="ti ti-file-spreadsheet text-success fs-14" />
                                    </div>
                                    <div className="text-start">
                                      <div className="fw-medium">
                                        Export Selected as Excel
                                      </div>
                                    </div>
                                  </button>
                                </li>
                              </>
                            )}

                            {selectedRows.length === 0 && (
                              <li>
                                <div className="text-center py-4 px-3">
                                  <i className="ti ti-checkbox fs-32 text-muted d-block mb-2" />
                                  <small className="text-muted">
                                    Select rows to export specific records
                                  </small>
                                </div>
                              </li>
                            )}
                          </ul>
                        </div>

                        <Link
                          to="#"
                          className="btn btn-primary"
                          data-bs-toggle="modal"
                          data-bs-target="#add_calls"
                        >
                          <i className="ti ti-square-rounded-plus me-2" />
                          Add New Lead GivenBy
                        </Link>
                      </div>
                    </div>
                  </div>
                  {/* /Search */}
                </div>

                <div className="d-flex align-items-center justify-content-between mb-1 flex-wrap gap-2">
                  <div className="d-flex align-items-center gap-2">
                    <div className="dropdown ms-4">
                      <Link
                        to="#"
                        className="dropdown-toggle"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-sort-ascending-2 me-2" />
                        Sort
                      </Link>
                      <div className="dropdown-menu dropdown-menu-start">
                        <ul className="list-unstyled mb-0">
                          {[
                            { label: "Ascending", value: "ascending" },
                            { label: "Descending", value: "descending" },
                            {
                              label: "Recently Added",
                              value: "recently_added",
                            },
                          ].map(({ label, value }) => {
                            const isActive = sortOption === value;
                            return (
                              <li key={value}>
                                <Link
                                  to="#"
                                  className={`dropdown-item d-flex justify-content-between align-items-center ${
                                    isActive ? "fw-bold text-primary" : ""
                                  }`}
                                  onClick={() => setSortOption(value)}
                                >
                                  <span>
                                    <i className="ti ti-circle-chevron-right me-1" />
                                    {label}
                                  </span>
                                  {isActive && <i className="ti ti-check" />}
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>

                    {/* Date Range Picker */}
                    <DateRangePicker
                      initialSettings={initialSettings}
                      onCallback={(start, end, label) => {
                        setSelectedDateRange([start.toDate(), end.toDate()]);
                        if (label) setActiveRangeLabel(label);
                      }}
                    >
                      <input
                        className="form-control bookingrange"
                        type="text"
                        readOnly
                        style={{ minWidth: "200px" }}
                      />
                    </DateRangePicker>

                    <div className="dropdown ms-1">
                      <Link
                        to="#"
                        className="dropdown-toggle d-flex align-items-center gap-2 fw-medium rounded border bg-white px-3 py-2"
                        data-bs-toggle="dropdown"
                      >
                        <i className="ti ti-filter-check fs-15 text-muted" />
                        <span>Status</span>
                        <span
                          className="text-capitalize ms-auto text-primary fw-semibold"
                          style={{ fontSize: "13px" }}
                        >
                          {statusFilter === "all"
                            ? "All"
                            : statusFilter === "active"
                            ? "Active"
                            : "Inactive"}
                        </span>
                      </Link>

                      <div className="dropdown-menu dropdown-menu-start shadow-sm border-0 py-1">
                        <ul className="list-unstyled mb-0">
                          {[
                            {
                              label: "All",
                              value: "all",
                              icon: "ti ti-list-details",
                              count: dataGivenBy.length,
                            },
                            {
                              label: "Active",
                              value: "active",
                              icon: "ti ti-check",
                              color: "text-success",
                              count: activeCount,
                            },
                            {
                              label: "Inactive",
                              value: "inactive",
                              icon: "ti ti-x",
                              color: "text-danger",
                              count: inactiveCount,
                            },
                          ].map(({ label, value, icon, color = "", count }) => {
                            const isActive = statusFilter === value;

                            return (
                              <li key={value}>
                                <Link
                                  to="#"
                                  className={`dropdown-item d-flex justify-content-between align-items-center gap-2 py-2 px-3 rounded ${
                                    isActive
                                      ? "bg-primary text-white fw-semibold"
                                      : "text-dark hover-bg-light"
                                  }`}
                                  onClick={() => setStatusFilter(value)}
                                  style={{ fontSize: "14px" }}
                                >
                                  <span className="d-flex align-items-center gap-2">
                                    <i
                                      className={`ti ${icon} fs-14 ${color} ${
                                        isActive ? "text-white" : ""
                                      }`}
                                    />
                                    {label}
                                  </span>

                                  <span className="d-flex align-items-center gap-2">
                                    <span
                                      className={`badge rounded-pill px-2 py-1 fw-medium text-xs ${
                                        isActive
                                          ? "bg-white text-primary"
                                          : value === "all"
                                          ? "bg-secondary text-white"
                                          : value === "active"
                                          ? "bg-success text-white"
                                          : "bg-danger text-white"
                                      }`}
                                    >
                                      {count}
                                    </span>
                                    {isActive && (
                                      <i className="ti ti-check fs-13" />
                                    )}
                                  </span>
                                </Link>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    </div>
                  </div>

                  {/* Right side: Manage Columns */}
                  <div className="d-flex align-items-center gap-2 flex-wrap">
                    {selectedRows.length > 0 && (
                      <button
                        type="button"
                        className={`btn btn-outline-danger d-flex align-items-center gap-2 ${
                          selectedRows.length === 0
                            ? "opacity-50 cursor-not-allowed"
                            : ""
                        }`}
                        data-bs-toggle="modal"
                        data-bs-target="#bulk_delete_modal"
                        disabled={selectedRows.length === 0}
                      >
                        <i className="ti ti-trash fs-16" />
                        <span className="d-none d-sm-inline">
                          Delete Selected
                        </span>
                        {selectedRows.length > 0 && (
                          <span className="badge bg-danger rounded-pill ms-2">
                            {selectedRows.length}
                          </span>
                        )}
                      </button>
                    )}
                    <div className="dropdown me-2">
                      {/* <div className="d-flex align-items-center gap-2 ms-auto">
  <button
    className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-outline-primary'}`}
    onClick={() => setViewMode('list')}
  >
    <i className="ti ti-list"></i> List
  </button>
  <button
    className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-outline-primary'}`}
    onClick={() => setViewMode('grid')}
  >
    <i className="ti ti-layout-grid"></i> Grid
  </button>
</div> */}
                      <Link
                        to="#"
                        className="btn bg-soft-purple text-purple"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                      >
                        <i className="ti ti-columns-3 me-2" />
                        Manage Columns
                      </Link>
                      <div
                        className="dropdown-menu dropdown-menu-end dropdown-md p-3"
                        style={{ minWidth: "250px" }}
                      >
                        <h4 className="mb-2 fw-semibold">Manage Columns</h4>
                        <p className="mb-3">
                          Drag and drop columns to reorder and toggle
                          visibility.
                        </p>
                        <div className="border-top pt-3">
                          {/* {Object.keys(visibleColumns).map((colKey) => (
                            <div
                              className="d-flex align-items-center justify-content-between mb-3"
                              key={colKey}
                            >
                              <p className="mb-0 d-flex align-items-center text-capitalize">
                                <i className="ti ti-grip-vertical me-2" />
                                {colKey.replace(/_/g, " ")}
                              </p>
                              <div className="status-toggle">
                                <input
                                  type="checkbox"
                                  className="check"
                                  id={`col-${colKey}`}
                                  checked={
                                    visibleColumns[
                                      colKey as keyof typeof visibleColumns
                                    ]
                                  }
                                  onChange={() => handleColumnToggle(colKey)}
                                />
                                <label
                                  htmlFor={`col-${colKey}`}
                                  className="checktoggle"
                                />
                              </div>
                            </div>
                          ))} */}
                          <ColumnManagement
  fixedKeys={["action"]}
  visibleColumns={visibleColumns}
  onColumnToggle={handleColumnToggle}
  onColumnOrderChange={(newOrder: string[]) => setColumnKeys(newOrder)}
  columnKeys={columnKeys}
/>
                                                {/* <ColumnManagement
                        columnKeys={columnKeys}
                        visibleColumns={visibleColumns}
                        onColumnOrderChange={setColumnKeys}
                        onColumnToggle={handleColumnToggle}
                        fixedKeys={["action"]}
                      /> */}

                        </div>
                      </div>
                    </div>
                    <div className="form-sorts dropdown me-4">
                      <Link
                        to="#"
                        className="dropdown-toggle dropdown-active-toggle d-flex align-items-center gap-2 fw-medium"
                        data-bs-toggle="dropdown"
                        data-bs-auto-close="outside"
                      >
                        <i className="ti ti-filter-share" /> Filter
                        {(filterNames.length > 0 ||
                          filterStatuses.length > 0 ||
                          filterCreatedAts.length > 0) && (
                          <span className="badge bg-primary rounded-pill ms-2">
                            {filterNames.length +
                              filterStatuses.length +
                              filterCreatedAts.length}
                          </span>
                        )}
                      </Link>

                      <div className="filter-dropdown-menu dropdown-menu dropdown-menu-md-end p-3">
                        <div className="filter-set-view">
                          <div className="filter-set-head">
                            <h4>
                              <i className="ti ti-filter-share" /> Filter
                            </h4>
                          </div>

                          <div className="accordion" id="accordionExample">
                            {/* Lead GivenBy Name Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head d-flex align-items-center gap-2">
                                <Link
                                  to="#"
                                  className={
                                    openAccordion === "leadGivenByName"
                                      ? ""
                                      : "collapsed"
                                  }
                                  onClick={() =>
                                    handleAccordionToggle("leadGivenByName")
                                  }
                                >
                                  Lead GivenBy Name
                                </Link>
                                {filterNames.length > 0 && (
                                  <span className="badge bg-primary rounded-pill small">
                                    {filterNames.length}
                                  </span>
                                )}
                              </div>
                              {openAccordion === "leadGivenByName" && (
                                <div className="filter-set-contents accordion-collapse collapse show">
                                  <div className="filter-content-list">
                                    <div className="mb-2 icon-form">
                                      <span className="form-icon">
                                        <i className="ti ti-search" />
                                      </span>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search Lead GivenBy"
                                        value={filterNamesSearch}
                                        onChange={(e) =>
                                          setFilterNamesSearch(e.target.value)
                                        }
                                      />
                                    </div>

                                    <ul className="list-unstyled mb-0">
                                      {uniqueNames
                                        .filter((n) =>
                                          n
                                            .toLowerCase()
                                            .includes(
                                              filterNamesSearch.toLowerCase()
                                            )
                                        )
                                        .map((name) => (
                                          <li key={name}>
                                            <div className="filter-checks">
                                              <label className="checkboxs">
                                                <input
                                                  type="checkbox"
                                                  checked={filterNames.includes(
                                                    name
                                                  )}
                                                  onChange={() =>
                                                    handleFilterChange(
                                                      name,
                                                      filterNames,
                                                      setFilterNames
                                                    )
                                                  }
                                                />
                                                <span className="checkmarks" />{" "}
                                                {name}
                                              </label>
                                            </div>
                                          </li>
                                        ))}

                                      {uniqueNames.filter((n) =>
                                        n
                                          .toLowerCase()
                                          .includes(
                                            filterNamesSearch.toLowerCase()
                                          )
                                      ).length === 0 && (
                                        <p className="text-muted px-2">
                                          No data found
                                        </p>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>

                            <hr className="my-2" />

                            {/* Status Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head d-flex align-items-center gap-2">
                                <Link
                                  to="#"
                                  className={
                                    openAccordion === "status"
                                      ? ""
                                      : "collapsed"
                                  }
                                  onClick={() =>
                                    handleAccordionToggle("status")
                                  }
                                >
                                  Status
                                </Link>
                                {filterStatuses.length > 0 && (
                                  <span className="badge bg-primary rounded-pill small">
                                    {filterStatuses.length}
                                  </span>
                                )}
                              </div>
                              {openAccordion === "status" && (
                                <div className="filter-set-contents accordion-collapse collapse show">
                                  <div className="filter-content-list">
                                    <div className="mb-2 icon-form">
                                      <span className="form-icon">
                                        <i className="ti ti-search" />
                                      </span>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search Status"
                                        value={filterStatusesSearch}
                                        onChange={(e) =>
                                          setFilterStatusesSearch(
                                            e.target.value
                                          )
                                        }
                                      />
                                    </div>

                                    <ul className="list-unstyled mb-0">
                                      {uniqueStatuses
                                        .filter((s) =>
                                          s
                                            .toLowerCase()
                                            .includes(
                                              filterStatusesSearch.toLowerCase()
                                            )
                                        )
                                        .map((status) => (
                                          <li key={status}>
                                            <div className="filter-checks">
                                              <label className="checkboxs">
                                                <input
                                                  type="checkbox"
                                                  checked={filterStatuses.includes(
                                                    status
                                                  )}
                                                  onChange={() =>
                                                    handleFilterChange(
                                                      status,
                                                      filterStatuses,
                                                      setFilterStatuses
                                                    )
                                                  }
                                                />
                                                <span className="checkmarks" />{" "}
                                                {status}
                                              </label>
                                            </div>
                                          </li>
                                        ))}

                                      {uniqueStatuses.filter((s) =>
                                        s
                                          .toLowerCase()
                                          .includes(
                                            filterStatusesSearch.toLowerCase()
                                          )
                                      ).length === 0 && (
                                        <p className="text-muted px-2">
                                          No data found
                                        </p>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>

                            <hr className="my-2" />

                            {/* Created At Filter */}
                            <div className="filter-set-content">
                              <div className="filter-set-content-head d-flex align-items-center gap-2">
                                <Link
                                  to="#"
                                  className={
                                    openAccordion === "createdAt"
                                      ? ""
                                      : "collapsed"
                                  }
                                  onClick={() =>
                                    handleAccordionToggle("createdAt")
                                  }
                                >
                                  Created At
                                </Link>
                                {filterCreatedAts.length > 0 && (
                                  <span className="badge bg-primary rounded-pill small">
                                    {filterCreatedAts.length}
                                  </span>
                                )}
                              </div>
                              {openAccordion === "createdAt" && (
                                <div className="filter-set-contents accordion-collapse collapse show">
                                  <div className="filter-content-list">
                                    <div className="mb-2 icon-form">
                                      <span className="form-icon">
                                        <i className="ti ti-search" />
                                      </span>
                                      <input
                                        type="text"
                                        className="form-control"
                                        placeholder="Search Created At"
                                        value={createdAtSearch}
                                        onChange={(e) =>
                                          setCreatedAtSearch(e.target.value)
                                        }
                                      />
                                    </div>

                                    <ul className="list-unstyled mb-0">
                                      {uniqueCreatedAts
                                        .filter((d) =>
                                          d
                                            .toLowerCase()
                                            .includes(
                                              createdAtSearch.toLowerCase()
                                            )
                                        )
                                        .map((date) => (
                                          <li key={date}>
                                            <div className="filter-checks">
                                              <label className="checkboxs">
                                                <input
                                                  type="checkbox"
                                                  checked={filterCreatedAts.includes(
                                                    date
                                                  )}
                                                  onChange={() =>
                                                    handleFilterChange(
                                                      date,
                                                      filterCreatedAts,
                                                      setFilterCreatedAts
                                                    )
                                                  }
                                                />
                                                <span className="checkmarks" />{" "}
                                                {date}
                                              </label>
                                            </div>
                                          </li>
                                        ))}

                                      {uniqueCreatedAts.filter((d) =>
                                        d
                                          .toLowerCase()
                                          .includes(
                                            createdAtSearch.toLowerCase()
                                          )
                                      ).length === 0 && (
                                        <p className="text-muted px-2">
                                          No data found
                                        </p>
                                      )}
                                    </ul>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>

                          {/* Reset / Apply Buttons */}
                          <div className="filter-reset-btns mt-3">
                            <div className="row">
                              <div className="col-6">
                                <Link
                                  to="#"
                                  className="btn btn-light w-100"
                                  onClick={() => {
                                    setFilterNames([]);
                                    setFilterStatuses([]);
                                    setFilterCreatedAts([]);
                                    setFilterNamesSearch("");
                                    setFilterStatusesSearch("");
                                    setCreatedAtSearch("");
                                    setOpenAccordion(null);
                                  }}
                                >
                                  Reset
                                </Link>
                              </div>
                              <div className="col-6">
                                <Link
                                  to="#"
                                  className="btn btn-primary w-100"
                                  data-bs-dismiss="dropdown"
                                >
                                  Apply
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                <div className="card-body">
                  {/* Table */}
                  <div className="table-responsive custom-table">
                   {error ? (
    <div className="text-center text-danger my-4">{error}</div>
  ) : viewMode === 'list' ? (
                    <Table
  columns={columns
    .sort(
      (a, b) =>
        columnKeys.indexOf(a.key as string) -
        columnKeys.indexOf(b.key as string)
    )
    .filter(
      (col) =>
        visibleColumns[col.key as keyof typeof visibleColumns]
    )}
  dataSource={filteredData} // Or orderedData if persisting row order
  rowKey="leadgivenby_id"
  loading={loading}
  enableRowSelection={true}
  className="custom-table"
  emptyText="No Lead Given By found"
  emptyIcon="ti ti-database-off"
  recordLabel="Lead Given By"
  pagination={{
    current: currentPage,
    showSizeChanger: true,
    pageSizeOptions: ["10", "20", "50", "75", "100"],
    pageSize: pageSize,
    onChange: (page, size) => {
      setCurrentPage(page);
      setPageSize(size);
    },
  }}
  rowSelection={{
    type: "checkbox",
    selectedRowKeys: selectedRows,
    onChange: (selectedRowKeys: React.Key[]) => {
      setSelectedRows(selectedRowKeys as string[]);
    },
  }}
  draggable={true} // Enable row drag
  enableColumnDrag={true} // Enable column drag
  onRowOrderChange={handleRowOrderChange}
  onColumnOrderChange={(newCols: any[]) => {
    // Get the new order of non-sticky columns from the dragged columns
    const newOrderedKeys = newCols
      .filter((c) => !c.fixed) // Only non-sticky columns
      .map((c) => c.key || c.dataIndex)
      .filter((key) => key !== undefined && key !== null && key !== ""); // Remove invalid keys

    // Preserve columns that are not in newCols (hidden columns) by merging with existing columnKeys
    const existingKeys = columnKeys;
    const allNonStickyKeys = defaultColumnOrder; // All possible non-sticky column keys

    // Create a new array that:
    // 1. Uses the new order for visible columns
    // 2. Preserves the order of hidden columns that aren't in newCols
    const preservedKeys = existingKeys.filter(
      (key) => !newOrderedKeys.includes(key)
    );
    const finalKeys = [...newOrderedKeys, ...preservedKeys];

    // Ensure all default columns are included (in case some are missing)
    const missingKeys = allNonStickyKeys.filter(
      (key) => !finalKeys.includes(key)
    );
    const completeKeys = [...finalKeys, ...missingKeys];

    setColumnKeys(completeKeys);
    // Don't auto-save, let user decide on navigation/refresh
  }}
  scroll={{ x: "max-content" }}
  components={{
   
  }}
/>) : (
    <GridView
  data={filteredData}
  renderCard={(item: LeadGivenBy, index: number) => (
    <div 
      key={index} 
      className="col"
    >
      <div className="grid-item p-3 border rounded shadow-sm bg-white hover-shadow transition-all h-100 d-flex flex-column">
        <div className="d-flex align-items-center gap-3 mb-3">
          <div 
            className="avatar avatar-md bg-primary-subtle text-primary rounded-circle d-flex align-items-center justify-content-center fw-bold fs-5"
            style={{ minWidth: 52, height: 52 }}
          >
            {item.LeadGivenBy_name?.charAt(0)?.toUpperCase() || "?"}
          </div>
          
          <div className="flex-grow-1 min-w-0">
            <h6 className="mb-1 fw-semibold text-truncate" title={item.LeadGivenBy_name || ""}>
              {item.LeadGivenBy_name || "—"}
            </h6>
            <div className="text-muted small">
              Created: {item.created_at 
                ? new Date(item.created_at).toLocaleDateString('en-IN', {
                    day: '2-digit',
                    month: 'short',
                    year: 'numeric'
                  }) 
                : "—"}
            </div>
          </div>
        </div>

        <div className="mt-auto">
          <AnimatedBadge
            status={item.Lead_status || "N/A"}
            className={`badge-status w-100 text-center py-2 fs-6 ${
              item.Lead_status?.toLowerCase() === "active" 
                ? "bg-success" 
                : item.Lead_status?.toLowerCase() === "inactive"
                  ? "bg-danger"
                  : "bg-secondary"
            }`}
          />
        </div>

        {/* Optional: quick action buttons at bottom */}
        {/* 
        <div className="mt-3 d-flex gap-2">
          <button 
            className="btn btn-sm btn-outline-primary flex-fill"
            onClick={() => handleEditClick(item)}
            data-bs-toggle="modal"
            data-bs-target="#edit_calls"
          >
            <i className="ti ti-edit me-1"></i> Edit
          </button>
          <button 
            className="btn btn-sm btn-outline-danger flex-fill"
            onClick={() => {
              setDeleteLeadGivenById(item.leadgivenby_id!);
              // open delete modal if needed
            }}
            data-bs-toggle="modal"
            data-bs-target="#delete_calls"
          >
            <i className="ti ti-trash me-1"></i> Delete
          </button>
        </div>
        */}
      </div>
    </div>
  )}
  className="row row-cols-1 row-cols-sm-2 row-cols-md-3 row-cols-lg-4 g-3"
/>
  )}
                  </div>

                  {/* /Table */}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* /Page Wrapper */}
      {/* Add New lead given by */}
      <div className="modal fade" id="add_calls">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-primary fw-bold fw-semibold"
                id="addLeadGivenByLabel"
              >
                <i className="ti ti-square-rounded-plus-filled me-2 fs-20" />
                Add New Lead GivenBy
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleCreateLeadGivenBy();
              }}
            >
              <div className="modal-body">
                <div className="mb-3">
                  <label
                    className={`col-form-label fw-semibold ${
                      addNameError ? "pro-shake text-danger" : "text-dark"
                    }`}
                    htmlFor="lead-source-input"
                  >
                    Lead GivenBy <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="lead-source-input"
                    className={`form-control form-control-lg ${
                      addNameError ? "is-invalid border-danger" : ""
                    }`}
                    value={newName}
                    onChange={(e) => {
                      const val = e.target.value;
                      setNewName(val);
                      const res = validateLeadGivenByName(val);
                      setAddNameError(res.valid ? "" : res.message);
                      setIsCreateValid(res.valid);

                      const label = document.querySelector(
                        '#add_calls label[for="lead-source-input"]'
                      ) as HTMLElement;
                      if (label) {
                        label.classList.remove("pro-shake");
                        if (!res.valid) {
                          void label.offsetHeight;
                          label.classList.add("pro-shake");
                        }
                      }
                    }}
                    placeholder="Enter Lead GivenBy name"
                    autoFocus
                  />
                  {addNameError && (
                    <div className="invalid-feedback fw-medium mt-2">
                      {addNameError}
                    </div>
                  )}
                </div>
                <div className="mb-0">
                  <label className="col-form-label d-block">Status</label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="add-active"
                        name="add-status"
                        checked={newStatus === "Active"}
                        onChange={() => setNewStatus("Active")}
                      />
                      <label className="form-check-label" htmlFor="add-active">
                        Active
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="add-inactive"
                        name="add-status"
                        checked={newStatus === "Inactive"}
                        onChange={() => setNewStatus("Inactive")}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="add-inactive"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="d-flex align-items-center justify-content-end m-0 w-100">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleCreateLeadGivenBy}
                    data-bs-dismiss="modal"
                    disabled={!isCreateValid || saving}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Creating...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check me-2" />
                        Create Lead GivenBy
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>

      {/* Add New Lead Given By */}
      {/* Edit Lead Given By */}
      <div className="modal fade" id="edit_calls">
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-primary fw-bold fw-semibold"
                id="editLeadGivenByLabel"
              >
                <i className="ti ti-edit me-2 fs-20" />
                Edit Lead GivenBy
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                handleSaveEdit();
              }}
            >
              <div className="modal-body">
                <div className="mb-3">
                  <label
                    className={`col-form-label fw-semibold ${
                      editNameError ? "pro-shake text-danger" : "text-dark"
                    }`}
                    htmlFor="edit-lead-source-input"
                  >
                    Lead GivenBy <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    id="edit-lead-source-input"
                    className={`form-control form-control-lg ${
                      editNameError ? "is-invalid border-danger" : ""
                    }`}
                    value={editName}
                    onChange={(e) => {
                      const val = e.target.value;
                      setEditName(val);
                      const res = validateLeadGivenByName(val, true);
                      setEditNameError(res.valid ? "" : res.message);

                      const label = document.querySelector(
                        '#edit_calls label[for="edit-lead-source-input"]'
                      ) as HTMLElement;
                      if (label) {
                        label.classList.remove("pro-shake");
                        if (!res.valid) {
                          void label.offsetHeight;
                          label.classList.add("pro-shake");
                        }
                      }
                    }}
                    placeholder="Enter Lead GivenBy name"
                    autoFocus
                  />
                  {editNameError && (
                    <div className="invalid-feedback fw-medium mt-2">
                      {editNameError}
                    </div>
                  )}
                </div>
                <div className="mb-0">
                  <label className="col-form-label d-block">Status</label>
                  <div className="d-flex align-items-center gap-3">
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="edit-active"
                        name="edit-status"
                        checked={editStatus === "Active"}
                        onChange={() => setEditStatus("Active")}
                      />
                      <label className="form-check-label" htmlFor="edit-active">
                        Active
                      </label>
                    </div>
                    <div className="form-check">
                      <input
                        className="form-check-input"
                        type="radio"
                        id="edit-inactive"
                        name="edit-status"
                        checked={editStatus === "Inactive"}
                        onChange={() => setEditStatus("Inactive")}
                      />
                      <label
                        className="form-check-label"
                        htmlFor="edit-inactive"
                      >
                        Inactive
                      </label>
                    </div>
                  </div>
                </div>
              </div>
              <div className="modal-footer">
                <div className="d-flex align-items-center justify-content-end m-0 w-100">
                  <button
                    type="button"
                    className="btn btn-light me-2"
                    data-bs-dismiss="modal"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={handleSaveEdit}
                    data-bs-dismiss="modal"
                    disabled={!editName.trim() || saving}
                  >
                    {saving ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                        />
                        Saving...
                      </>
                    ) : (
                      <>
                        <i className="ti ti-check me-2" />
                        Save Changes
                      </>
                    )}
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
      {/* /Edit Calls */}
      {/* Delete Calls */}
      <div
        className="modal fade"
        id="delete_calls"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="deleteCallsLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-danger fw-bold fw-semibold"
                id="deleteCallsLabel"
              >
                <i className="ti ti-alert-triangle me-2" />
                Confirm Deletion
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body text-center ">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-4 mx-auto">
                <i className="ti ti-trash fs-40 text-danger" />
              </div>
              <h4 className="fw-bold text-dark mb-3">
                Delete This Lead GivenBy?
              </h4>
              <p className="text-muted fs-15 mb-4 px-4">
                This action <strong>cannot be undone</strong>. The lead given by
                will be
                <strong> permanently removed</strong>.
              </p>
              <div className="alert alert-warning py-3 mx-4 mb-4 rounded-3 border-warning">
                <i className="ti ti-info-circle me-2" />
                <strong>Warning:</strong> This may affect leads currently linked
                to this given by.
              </div>
              <div className="d-flex gap-3 justify-content-center mt-4">
                <button
                  type="button"
                  className="btn btn-light px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                  onClick={handleConfirmDelete}
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                        aria-hidden="true"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-trash me-2" />
                      Yes, Delete It
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* /Delete Calls */}
      {/* Bulk Delete Modal */}
      <div
        className="modal fade"
        id="bulk_delete_modal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="bulkDeleteLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-danger fw-bold"
                id="bulkDeleteLabel"
              >
                <i className="ti ti-alert-triangle me-2" />
                Confirm Bulk Deletion
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>
            <div className="modal-body text-center">
              <div className="avatar avatar-xl bg-danger-light rounded-circle mb-4 mx-auto">
                <i className="ti ti-trash fs-40 text-danger" />
              </div>
              <h4 className="fw-bold text-dark mb-3">
                Delete {selectedRows.length} Lead Given By
                {selectedRows.length > 1 ? " entries" : ""}?
              </h4>
              <p className="text-muted fs-15 mb-4 px-4">
                This action <strong>cannot be undone</strong>. The selected
                entries will be
                <strong> permanently removed</strong> from your system.
              </p>
              <div className="alert alert-warning py-3 mx-4 mb-4 rounded-3 border-warning">
                <i className="ti ti-info-circle me-2" />
                <strong>Warning:</strong> This may affect existing leads.
              </div>
              <div className="d-flex gap-3 justify-content-center mt-4">
                <button
                  type="button"
                  className="btn btn-light px-5 py-2 fw-medium"
                  data-bs-dismiss="modal"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn btn-danger px-5 py-2 fw-medium"
                  onClick={handleBulkDelete}
                  data-bs-dismiss="modal"
                  disabled={deleting}
                >
                  {deleting ? (
                    <>
                      <span
                        className="spinner-border spinner-border-sm me-2"
                        role="status"
                      />
                      Deleting...
                    </>
                  ) : (
                    <>
                      <i className="ti ti-trash me-2" />
                      Yes, Delete Permanently
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Import Excel Modal */}
      <div
        className="modal fade"
        id="import_excel_modal"
        tabIndex={-1}
        role="dialog"
        aria-labelledby="importExcelLabel"
        aria-hidden="true"
      >
        <div className="modal-dialog modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5
                className="modal-title text-success fw-bold d-flex align-items-center gap-2"
                id="importExcelLabel"
              >
                <i className="ti ti-file-import fs-22" />
                Import Lead Given By
              </h5>
              <button
                type="button"
                className="btn-close custom-btn-close border p-1 me-0 d-flex align-items-center justify-content-center rounded-circle"
                data-bs-dismiss="modal"
                aria-label="Close"
              >
                <i className="ti ti-x" />
              </button>
            </div>

            <div className="modal-body">
              {!importResult ? (
                <>
                  <div
                    className={`border-2 rounded-3 p-4 text-center transition-all ${
                      importFile
                        ? "border-success bg-success-light"
                        : "border-dashed border-success bg-success-subtle"
                    }`}
                    style={{
                      cursor: "pointer",
                      minHeight: "180px",
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      alignItems: "center",
                    }}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.add(
                        "bg-success-light",
                        "border-success"
                      );
                    }}
                    onDragLeave={(e) => {
                      e.currentTarget.classList.remove(
                        "bg-success-light",
                        "border-success"
                      );
                      if (!importFile)
                        e.currentTarget.classList.add("border-dashed");
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      e.currentTarget.classList.remove(
                        "bg-success-light",
                        "border-success"
                      );
                      const file = e.dataTransfer.files[0];
                      if (file && file.name.toLowerCase().endsWith(".xlsx")) {
                        setImportFile(file);
                      } else {
                        toast.error("Please upload a valid .xlsx file");
                      }
                    }}
                  >
                    <input
                      type="file"
                      accept=".xlsx"
                      className="d-none"
                      id="excel-upload"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setImportFile(file);
                      }}
                    />

                    <i
                      className={`ti ti-file-upload fs-40 mb-3 ${
                        importFile ? "text-success" : "text-success"
                      }`}
                    />

                    {importFile ? (
                      <div className="text-center">
                        <p className="fw-bold text-dark mb-1">
                          {importFile.name}
                        </p>
                        <p className="text-muted small mb-3">
                          {importFile.size > 1024 * 1024
                            ? (importFile.size / (1024 * 1024)).toFixed(1) +
                              " MB"
                            : (importFile.size / 1024).toFixed(1) + " KB"}
                        </p>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger rounded-pill px-3"
                          onClick={(e) => {
                            e.stopPropagation();
                            setImportFile(null);
                          }}
                        >
                          <i className="ti ti-x fs-14 me-1" />
                          Remove
                        </button>
                      </div>
                    ) : (
                      <>
                        <p className="fw-semibold text-dark mb-2">
                          Drag & drop Excel file here
                        </p>
                        <p className="text-muted small mb-2">or</p>
                        <label
                          htmlFor="excel-upload"
                          className="btn btn-success rounded-pill px-4 py-2 shadow-sm"
                        >
                          <i className="ti ti-file-upload me-2" />
                          Browse File
                        </label>
                        <p className="text-muted small mt-3 mb-0">
                          Supports <code>.xlsx</code> format only
                        </p>
                      </>
                    )}
                  </div>

                  <div className="text-center mt-3">
                    <a
                      href="/leadgivenby.xlsx"
                      download="lead_given_by_template.xlsx"
                      className="text-primary small d-inline-flex align-items-center fw-medium hover-text-primary-dark transition-colors"
                    >
                      <i className="ti ti-file-download me-1" />
                      Download Sample Template (.xlsx)
                    </a>
                  </div>
                </>
              ) : (
                <div className="text-center">
                  <div
                    className="mx-auto mb-4 d-inline-flex align-items-center justify-content-center rounded-circle bg-success text-white shadow-lg"
                    style={{ width: "90px", height: "90px" }}
                  >
                    <i className="ti ti-check fs-48" />
                  </div>
                  <h5 className="fw-bold text-success mb-3">
                    Import Successful!
                  </h5>
                  <p className="text-dark fw-semibold mb-2">
                    <strong>{importResult.added.length}</strong> lead given by
                    {importResult.added.length !== 1 ? " entries" : ""} added
                  </p>
                  {importResult.skipped.length > 0 && (
                    <p className="text-danger fw-medium mb-0 d-flex align-items-center justify-content-center gap-2">
                      <i className="ti ti-alert-triangle fs-16" />
                      <strong>{importResult.skipped.length}</strong> row
                      {importResult.skipped.length !== 1 ? "s" : ""} skipped due
                      to errors
                    </p>
                  )}
                </div>
              )}
            </div>

            <div className="modal-footer">
              <div className="d-flex justify-content-end gap-3 w-100">
                {!importResult ? (
                  <>
                    <button
                      type="button"
                      className="btn btn-light px-4 py-2 rounded-3"
                      data-bs-dismiss="modal"
                      onClick={() => setImportFile(null)}
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      className="btn btn-success px-5 py-2 rounded-3 d-flex align-items-center gap-2 shadow-sm"
                      disabled={!importFile || importing}
                      onClick={async () => {
                        if (!importFile) return;
                        setImporting(true);
                        try {
                          const result = await importLeadGivenByExcel(
                            importFile
                          );
                          setImportResult(result);
                          await loadLeadGivenBy(); // your refresh function
                        } catch (err) {
                          // toast handled in API
                        } finally {
                          setImporting(false);
                        }
                      }}
                    >
                      {importing ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" />
                          Importing Lead Given By...
                        </>
                      ) : (
                        <>
                          <i className="ti ti-table-import" />
                          Import File
                        </>
                      )}
                    </button>
                  </>
                ) : (
                  <button
                    type="button"
                    className="btn btn-primary px-5 py-2 rounded-3 w-100"
                    data-bs-dismiss="modal"
                    onClick={() => {
                      setImportResult(null);
                      setImportFile(null);
                    }}
                  >
                    Done
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <SaveModal />
<ScrollToTop />
<ModalDeleteEffect />
    </>
  );
};

export default LeadsGivenBy;
